import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

const fdAppend = (fd, k, v) => {
  if (v === undefined || v === null || v === "") return;
  fd.append(k, v);
};

/* =========================
   MASTERS
========================= */

export const fetchIncomeSourcesThunk = createAsyncThunk(
  "incomeExpense/fetchIncomeSources",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/masters/income-sources");
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return res.data?.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const fetchExpenseTypesThunk = createAsyncThunk(
  "incomeExpense/fetchExpenseTypes",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/masters/expense-types");
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return res.data?.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

/* =========================
   LIST (POST FormData)
========================= */

// ✅ POST /api/income-expense/income/list
// Supports: status, from_date, to_date, page, limit, income_source_id, q, min_amount, max_amount
export const fetchIncomeListThunk = createAsyncThunk(
  "incomeExpense/fetchIncomeList",
  async (params = {}, { rejectWithValue }) => {
    try {
      const fd = new FormData();

      fdAppend(fd, "status", params?.status || "all");
      fdAppend(fd, "page", params?.page || 1);
      fdAppend(fd, "limit", params?.limit || 10);

      fdAppend(fd, "from_date", params?.from_date);
      fdAppend(fd, "to_date", params?.to_date);

      fdAppend(fd, "income_source_id", params?.income_source_id);
      fdAppend(fd, "q", params?.q);
      fdAppend(fd, "min_amount", params?.min_amount);
      fdAppend(fd, "max_amount", params?.max_amount);

      const res = await api.post("/api/income-expense/income/list", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");

      return {
        data: res.data?.data || [],
        pagination: res.data?.pagination || null,
        filters: res.data?.filters || null,
      };
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

// ✅ POST /api/income-expense/expense/list
// Supports: status, from_date, to_date, page, limit, expense_type_id, q, min_amount, max_amount
export const fetchExpenseListThunk = createAsyncThunk(
  "incomeExpense/fetchExpenseList",
  async (params = {}, { rejectWithValue }) => {
    try {
      const fd = new FormData();

      fdAppend(fd, "status", params?.status || "all");
      fdAppend(fd, "page", params?.page || 1);
      fdAppend(fd, "limit", params?.limit || 10);

      fdAppend(fd, "from_date", params?.from_date);
      fdAppend(fd, "to_date", params?.to_date);

      fdAppend(fd, "expense_type_id", params?.expense_type_id);
      fdAppend(fd, "q", params?.q);
      fdAppend(fd, "min_amount", params?.min_amount);
      fdAppend(fd, "max_amount", params?.max_amount);

      const res = await api.post("/api/income-expense/expense/list", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");

      return {
        data: res.data?.data || [],
        pagination: res.data?.pagination || null,
        filters: res.data?.filters || null,
      };
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

/* =========================
   CREATE (Income / Expense)
========================= */

export const createIncomeEntryThunk = createAsyncThunk(
  "incomeExpense/createIncome",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/income-expense/income", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const createExpenseEntryThunk = createAsyncThunk(
  "incomeExpense/createExpense",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/income-expense/expense", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

/* =========================
   APPROVE / REVOKE
========================= */

export const approveIncomeThunk = createAsyncThunk(
  "incomeExpense/approveIncome",
  async (income_id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/income-expense/income/${income_id}/approve`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return { income_id, ...res.data };
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const revokeIncomeThunk = createAsyncThunk(
  "incomeExpense/revokeIncome",
  async (income_id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/income-expense/income/${income_id}/revoke`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return { income_id, ...res.data };
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const approveExpenseThunk = createAsyncThunk(
  "incomeExpense/approveExpense",
  async (expense_id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/income-expense/expense/${expense_id}/approve`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return { expense_id, ...res.data };
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const revokeExpenseThunk = createAsyncThunk(
  "incomeExpense/revokeExpense",
  async (expense_id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/income-expense/expense/${expense_id}/revoke`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return { expense_id, ...res.data };
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

/* =========================
   SLICE
========================= */

const slice = createSlice({
  name: "incomeExpense",
  initialState: {
    incomeSources: [],
    expenseTypes: [],
    mastersLoading: false,

    incomeList: [],
    expenseList: [],
    incomePagination: null,
    expensePagination: null,
    listLoading: false,

    savingIncome: false,
    savingExpense: false,

    approving: false,

    error: null,
  },
  reducers: {
    clearIncomeExpenseError(state) {
      state.error = null;
    },
  },
  extraReducers: (b) => {
    b
      // ===== masters loading =====
      .addCase(fetchIncomeSourcesThunk.pending, (s) => {
        s.mastersLoading = true;
        s.error = null;
      })
      .addCase(fetchIncomeSourcesThunk.fulfilled, (s, a) => {
        s.mastersLoading = false;
        s.incomeSources = a.payload || [];
      })
      .addCase(fetchIncomeSourcesThunk.rejected, (s, a) => {
        s.mastersLoading = false;
        s.incomeSources = [];
        s.error = a.payload || "Failed to load income sources";
      })

      .addCase(fetchExpenseTypesThunk.pending, (s) => {
        s.mastersLoading = true;
        s.error = null;
      })
      .addCase(fetchExpenseTypesThunk.fulfilled, (s, a) => {
        s.mastersLoading = false;
        s.expenseTypes = a.payload || [];
      })
      .addCase(fetchExpenseTypesThunk.rejected, (s, a) => {
        s.mastersLoading = false;
        s.expenseTypes = [];
        s.error = a.payload || "Failed to load expense types";
      })

      // ===== list income / expense (POST) =====
      .addCase(fetchIncomeListThunk.pending, (s) => {
        s.listLoading = true;
        s.error = null;
      })
      .addCase(fetchIncomeListThunk.fulfilled, (s, a) => {
        s.listLoading = false;
        s.incomeList = a.payload?.data || [];
        s.incomePagination = a.payload?.pagination || null;
      })
      .addCase(fetchIncomeListThunk.rejected, (s, a) => {
        s.listLoading = false;
        s.incomeList = [];
        s.incomePagination = null;
        s.error = a.payload || "Failed to load income list";
      })

      .addCase(fetchExpenseListThunk.pending, (s) => {
        s.listLoading = true;
        s.error = null;
      })
      .addCase(fetchExpenseListThunk.fulfilled, (s, a) => {
        s.listLoading = false;
        s.expenseList = a.payload?.data || [];
        s.expensePagination = a.payload?.pagination || null;
      })
      .addCase(fetchExpenseListThunk.rejected, (s, a) => {
        s.listLoading = false;
        s.expenseList = [];
        s.expensePagination = null;
        s.error = a.payload || "Failed to load expense list";
      })

      // ===== income save =====
      .addCase(createIncomeEntryThunk.pending, (s) => {
        s.savingIncome = true;
        s.error = null;
      })
      .addCase(createIncomeEntryThunk.fulfilled, (s) => {
        s.savingIncome = false;
      })
      .addCase(createIncomeEntryThunk.rejected, (s, a) => {
        s.savingIncome = false;
        s.error = a.payload || "Failed to save income";
      })

      // ===== expense save =====
      .addCase(createExpenseEntryThunk.pending, (s) => {
        s.savingExpense = true;
        s.error = null;
      })
      .addCase(createExpenseEntryThunk.fulfilled, (s) => {
        s.savingExpense = false;
      })
      .addCase(createExpenseEntryThunk.rejected, (s, a) => {
        s.savingExpense = false;
        s.error = a.payload || "Failed to save expense";
      })

      // ===== approve/revoke income =====
      .addCase(approveIncomeThunk.pending, (s) => {
        s.approving = true;
        s.error = null;
      })
      .addCase(approveIncomeThunk.fulfilled, (s, a) => {
        s.approving = false;
        const id = Number(a.payload?.income_id || 0);
        const row = s.incomeList.find((x) => Number(x.income_id) === id);
        if (row) {
          row.approved = 1;
          row.approval_status = "approved";
        }
      })
      .addCase(approveIncomeThunk.rejected, (s, a) => {
        s.approving = false;
        s.error = a.payload || "Failed to approve income";
      })

      .addCase(revokeIncomeThunk.pending, (s) => {
        s.approving = true;
        s.error = null;
      })
      .addCase(revokeIncomeThunk.fulfilled, (s, a) => {
        s.approving = false;
        const id = Number(a.payload?.income_id || 0);
        const row = s.incomeList.find((x) => Number(x.income_id) === id);
        if (row) {
          row.approved = 0;
          row.approval_status = "pending";
          row.approved_by = null;
          row.approved_on = null;
        }
      })
      .addCase(revokeIncomeThunk.rejected, (s, a) => {
        s.approving = false;
        s.error = a.payload || "Failed to revoke income";
      })

      // ===== approve/revoke expense =====
      .addCase(approveExpenseThunk.pending, (s) => {
        s.approving = true;
        s.error = null;
      })
      .addCase(approveExpenseThunk.fulfilled, (s, a) => {
        s.approving = false;
        const id = Number(a.payload?.expense_id || 0);
        const row = s.expenseList.find((x) => Number(x.expense_id) === id);
        if (row) {
          row.approved = 1;
          row.approval_status = "approved";
        }
      })
      .addCase(approveExpenseThunk.rejected, (s, a) => {
        s.approving = false;
        s.error = a.payload || "Failed to approve expense";
      })

      .addCase(revokeExpenseThunk.pending, (s) => {
        s.approving = true;
        s.error = null;
      })
      .addCase(revokeExpenseThunk.fulfilled, (s, a) => {
        s.approving = false;
        const id = Number(a.payload?.expense_id || 0);
        const row = s.expenseList.find((x) => Number(x.expense_id) === id);
        if (row) {
          row.approved = 0;
          row.approval_status = "pending";
          row.approved_by = null;
          row.approved_on = null;
        }
      })
      .addCase(revokeExpenseThunk.rejected, (s, a) => {
        s.approving = false;
        s.error = a.payload || "Failed to revoke expense";
      });
  },
});

export const { clearIncomeExpenseError } = slice.actions;
export default slice.reducer;